/**
 * <burnout-compare>
 *
 * Before/after slider.
 *
 * The control is a real <input type="range"> stretched invisibly over the
 * image. That single decision buys drag, click-to-jump, keyboard arrows,
 * Home/End, touch, and correct screen-reader semantics — all from the
 * platform, none of it reimplemented. JS only mirrors the value into a CSS
 * custom property that drives the clip.
 *
 * On first scroll into view the handle sweeps once, because a static seam in
 * the middle of a photo does not read as draggable to most people.
 */
class BurnoutCompare extends HTMLElement {
  #input = null;
  #stage = null;
  #demoed = false;

  connectedCallback() {
    this.#input = this.querySelector('.bo-cmp__range');
    this.#stage = this.querySelector('.bo-cmp__stage');
    if (!this.#input || !this.#stage) return;

    this.#input.addEventListener('input', this.#sync);
    this.#sync();
    this.#initChipPulse();

    // A pointer press should also stop any running demo, so the sweep never
    // fights someone who has already grabbed the handle.
    this.#input.addEventListener('pointerdown', () => {
      this.#demoed = true;
    });

    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || this.#demoed) return;
          this.#demoed = true;
          observer.disconnect();
          this.#demo();
        });
      },
      { threshold: 0.45 }
    );
    observer.observe(this);
  }

  /**
   * Fire the chip's border animation in bursts on an interval.
   *
   * Done in JS rather than CSS because the visible window and the idle gap
   * have to stay in step with a merchant-set rotation count, and keyframe
   * percentages cannot be computed from settings — section {% stylesheet %}
   * blocks are static CSS, not Liquid.
   *
   * Bursts only run while the chip is actually on screen and the tab is
   * visible; there is no point animating something nobody is looking at.
   */
  #initChipPulse() {
    const chip = this.querySelector('.bo-cmp__product');
    if (!chip) return;

    const every = Number(chip.dataset.spinEvery) || 0;
    if (!every) return; // 0 means the merchant turned it off
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const rotations = Number(chip.dataset.spinRotations) || 2;
    const speed = Number(chip.dataset.spinSpeed) || 1.5;
    const burstMs = rotations * speed * 1000;

    let onScreen = false;
    let timer = null;

    const burst = () => {
      if (!onScreen || document.hidden) return;
      chip.classList.add('is-spinning');
      setTimeout(() => chip.classList.remove('is-spinning'), burstMs);
    };

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            onScreen = entry.isIntersecting;
            if (onScreen && !timer) {
              burst();
              timer = setInterval(burst, every * 1000);
            } else if (!onScreen && timer) {
              clearInterval(timer);
              timer = null;
            }
          });
        },
        { threshold: 0.3 }
      ).observe(chip);
    } else {
      onScreen = true;
      burst();
      setInterval(burst, every * 1000);
    }
  }

  #sync = () => {
    const value = Number(this.#input.value);
    this.#stage.style.setProperty('--bo-cmp-pos', `${value}%`);

    // Label opacity is computed here rather than with clamp()/calc() in CSS.
    // A clamp() whose middle term evaluates negative was resolving to the max
    // instead of the min in testing, which left both labels permanently at
    // full opacity. Doing the arithmetic in JS removes the ambiguity.
    const fade = (x) => Math.min(1, Math.max(0, x)).toFixed(3);
    this.#stage.style.setProperty('--bo-cmp-before-op', fade((value - 8) / 14));
    this.#stage.style.setProperty('--bo-cmp-after-op', fade((92 - value) / 14));
  };

  /** One slow sweep out and back, so the seam announces itself as a control. */
  #demo = () => {
    const start = performance.now();
    const from = Number(this.#input.value);
    const keys = [
      { at: 0, value: from },
      { at: 0.35, value: 72 },
      { at: 0.72, value: 30 },
      { at: 1, value: from },
    ];
    const duration = 2200;

    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // Ease so the reversals feel weighted rather than mechanical.
      const eased = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;

      let value = from;
      for (let i = 1; i < keys.length; i += 1) {
        const a = keys[i - 1];
        const b = keys[i];
        if (eased <= b.at || i === keys.length - 1) {
          const span = b.at - a.at || 1;
          const local = Math.min(1, Math.max(0, (eased - a.at) / span));
          value = a.value + (b.value - a.value) * local;
          break;
        }
      }

      this.#input.value = String(value);
      this.#sync();
      if (t < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  };
}

if (!customElements.get('burnout-compare')) {
  customElements.define('burnout-compare', BurnoutCompare);
}
